import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { PageTransition } from '@/components/PageTransition';
import { useSpaceData } from '@/hooks/useSpaceData';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import {
  ArrowLeft,
  Pencil,
  Link as LinkIcon,
  Download,
  File as FileIcon,
  Lock
} from 'lucide-react';

// Read-only Table Block
function TableBlockViewer({ data }) {
  const rows = data.rows || 3;
  const cols = data.cols || 3;
  const cells = data.cells || {};

  const getCellKey = (row, col) => `${row}-${col}`;

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse border border-gray-300">
        <tbody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: cols }).map((_, colIndex) => {
                const cellKey = getCellKey(rowIndex, colIndex);
                return (
                  <td
                    key={colIndex}
                    className={`border border-gray-300 px-3 py-2 min-w-[120px] ${
                      rowIndex === 0 ? 'bg-muted/30 font-medium' : ''
                    }`}
                  >
                    {cells[cellKey] || ''}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Read-only Embed Block
function EmbedBlockViewer({ data }) {
  const url = data.url;

  if (!url) return null;

  return (
    <div className="border rounded-lg overflow-hidden bg-muted/5">
      <div className="flex items-center gap-2 px-4 py-2 bg-muted/30 border-b text-sm text-muted-foreground">
        <LinkIcon className="h-4 w-4" />
        <span className="truncate">{url}</span>
      </div>
      <div className="relative w-full h-[600px] bg-white">
        <iframe
          src={url}
          className="w-full h-full border-0"
          title="Embedded content"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          loading="lazy"
        />
      </div>
    </div>
  );
}

// Content block renderer (read-only)
function ContentBlockViewer({ block }) {
  const renderContent = () => {
    switch (block.type) {
      case 'text':
        return (
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: block.content || '' }}
          />
        );

      case 'image':
        if (!block.content) return null;
        return (
          <img
            src={block.content}
            alt=""
            className="max-w-full rounded-lg"
          />
        );

      case 'video':
        if (!block.content) return null;

        const isEmbedUrl = (url) => {
          return url.includes('youtube.com/embed') ||
                 url.includes('youtu.be') ||
                 url.includes('vimeo.com');
        };

        return isEmbedUrl(block.content) ? (
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              src={block.content}
              className="absolute top-0 left-0 w-full h-full rounded-lg"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Video content"
            />
          </div>
        ) : (
          <video
            src={block.content}
            controls
            className="w-full h-auto rounded-lg"
          >
            Your browser does not support the video tag.
          </video>
        );

      case 'file':
        if (!block.content) return null;

        const fileData = typeof block.content === 'string'
          ? { url: block.content, name: block.filename || 'File', size: null }
          : block.content;

        const formatFileSize = (bytes) => {
          if (!bytes) return '';
          if (bytes < 1024) return bytes + ' B';
          if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
          return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        };

        const handleDownload = () => {
          const link = document.createElement('a');
          link.href = fileData.url;
          link.download = fileData.name || 'download';
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        };

        return (
          <div className="border rounded-lg p-4 bg-muted/30">
            <div className="flex items-center gap-3">
              <FileIcon className="h-8 w-8 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{fileData.name}</div>
                {fileData.size && (
                  <div className="text-xs text-muted-foreground">
                    {formatFileSize(fileData.size)}
                  </div>
                )}
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={handleDownload}
                title="Download file"
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case 'table':
        return <TableBlockViewer data={block.content || { rows: 3, cols: 3, cells: {} }} />;

      case 'embed':
        return <EmbedBlockViewer data={block.content || {}} />;

      default:
        return null;
    }
  };

  return (
    <div className="mb-6">
      {renderContent()}
    </div>
  );
}

export function PageViewerPage() {
  const navigate = useNavigate();
  const { projectId, pageId } = useParams();
  const { userProfile, company } = useAuth();
  // Pass company.id to validate the space belongs to the user's current company
  const { space, spaceTools, accessDenied } = useSpaceData(projectId, userProfile?.id, company?.id);

  // Set page title
  usePageTitle('Page');

  // Redirect if user doesn't have access (e.g., switched companies)
  useEffect(() => {
    if (accessDenied) {
      toast.error('You no longer have access to this space');
      navigate('/dashboard', { replace: true });
    }
  }, [accessDenied, navigate]);

  const [page, setPage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Check if current user is the author
  const isAuthor = page?.created_by === userProfile?.id;

  // Load page
  useEffect(() => {
    const loadPage = async () => {
      if (!pageId) {
        setNotFound(true);
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('pages')
          .select('*')
          .eq('id', pageId)
          .single();

        if (error) throw error;

        if (!data) {
          setNotFound(true);
        } else if (data.status !== 'published') {
          // Page is not published - redirect to editor if user has access
          toast.info('This page is not published yet');
          navigate(`/space/${projectId}/page-editor?id=${pageId}`);
          return;
        } else {
          setPage(data);
        }
      } catch (error) {
        console.error('Error loading page:', error);
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadPage();
  }, [pageId, projectId, navigate]);

  if (isLoading) {
    return (
      <AppShell3>
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner />
        </div>
      </AppShell3>
    );
  }

  if (notFound || !page) {
    return (
      <AppShell3>
        <PageTransition>
          <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
            <h1 className="text-2xl font-bold">Page Not Found</h1>
            <p className="text-muted-foreground">
              This page doesn't exist or hasn't been published yet.
            </p>
            <Button onClick={() => navigate(`/space/${projectId}/tasks`)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Tasks
            </Button>
          </div>
        </PageTransition>
      </AppShell3>
    );
  }

  return (
    <AppShell3 pageTitle="">
      <PageTransition>
        <ToolHeader
          spaceName={space?.name || 'Pages'}
          spaceImage={space?.logo_url}
          toolName={page.name}
          currentTool='tasks'
          spaceTools={spaceTools}
          parentBreadcrumb={{
            name: 'Tasks',
            onClick: () => navigate(`/space/${projectId}/tasks`)
          }}
          actions={
            <div className="flex items-center gap-3">
              {!isAuthor && (
                <div className="flex items-center gap-1.5 text-muted-foreground text-sm">
                  <Lock className="h-4 w-4" />
                  <span>Read only</span>
                </div>
              )}
              {isAuthor && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/space/${projectId}/page-editor?id=${pageId}`)}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
          }
        />

        <div className="min-h-screen bg-gray-100">
          {/* Cover Image */}
          {page.cover_image && (
            <div className="relative w-full h-80">
              <img
                src={page.cover_image}
                alt="Cover"
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Content */}
          <div className={`max-w-4xl mx-auto px-24 bg-white shadow-sm ${page.cover_image ? 'rounded-t-3xl -mt-16 relative z-10 pt-16 pb-12' : 'rounded-xl mt-6 py-12'}`}>
            {/* Title */}
            <h1 className="text-5xl font-bold mb-8" style={{ fontSize: '3rem', lineHeight: '1.2' }}>
              {page.name}
            </h1>

            {/* Content Blocks */}
            <div className="mt-4">
              {(page.content_blocks || []).map((block) => (
                <ContentBlockViewer key={block.id} block={block} />
              ))}

              {(!page.content_blocks || page.content_blocks.length === 0) && (
                <p className="text-muted-foreground text-center py-12">
                  This page has no content yet.
                </p>
              )}
            </div>
          </div>
        </div>
      </PageTransition>
    </AppShell3>
  );
}
